import type { SharedBudget, SharedBudgetPayload, SharedBudgetStats } from '@metapic-apps/shared'
import { onMounted, ref, watchEffect } from 'vue'

import {
  normalizeSharedBudgets,
  normalizeSharedBudgetStats,
  serializeSharedBudget,
} from '@/lib/adapters/shared-budgets.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error'

export const useGetSharedBudgets = (advertiserId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<SharedBudget[]>([])

  const errorStore = useErrorStore()

  const fetchData = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get(`/v2/stores/${advertiserId}/shared-budgets`)

      data.value = normalizeSharedBudgets(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }
  onMounted(() => {
    if (advertiserId) {
      return fetchData()
    }
  })

  return { data, fetchData, isLoading, error }
}

export const useEditSharedBudgetRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const editBudget = async (advertiserId: number, budgetData: SharedBudgetPayload) => {
    try {
      error.value = null
      isLoading.value = true

      await request.put(
        `/v2/stores/${advertiserId}/shared-budgets/${budgetData.id}`,
        serializeSharedBudget(budgetData),
      )
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    editBudget,
    isLoading,
    error,
  }
}

export const useDeleteSharedBudgetRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()

  const deleteBudget = async (advertiserId: number, id: number) => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/v2/stores/${advertiserId}/shared-budgets/${id}`)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    deleteBudget,
    isLoading,
    error,
  }
}

export const useGetSharedBudgetStats = ({
  advertiserId,
  budgetId,
}: {
  advertiserId: number
  budgetId: number
}) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const data = ref<SharedBudgetStats | undefined>(undefined)

  const getBudget = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await request.get(`/v2/stores/${advertiserId}/shared-budgets/${budgetId}`)
      data.value = normalizeSharedBudgetStats(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => void getBudget())

  return {
    data,
    getBudget,
    isLoading,
    error,
  }
}
